import { CreditRuleCoin } from '@aibees/elephant/components/credit-rule-coin';
import { usePersonReadDataContext } from '@aibees/elephant/context';
import { usePersonalityAnalysisStatus } from '@aibees/elephant/hooks/people/use-personality-analysis-status';
import {
  SaveRecordAction,
  SaveRecordRequest,
} from '@aibees/elephant/hooks/types';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { SearchType } from '@aibees/elephant/types';
import { notificationIconMap } from '@aibees/elephant/utils';

import { useIsPeopleUnlockButtonLoading } from '@aibees/elephant/components/people-unlock/hooks/use-is-people-unlock-button-loading';
import { QUICK_INFO_BUTTONS } from '@aibees/elephant/components/quick-info-group/components/quick-info-button';
import {
  ProcessingItemState,
  useActionButtonContext,
} from '@aibees/elephant/context/action-button-context';
import { useTrackEvents } from '@aibees/elephant/hooks';
import { CreditUsageType } from '@aibees/shared/definition';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { Stack, Typography } from '@mui/material';
import { memo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { PeopleAnalysisUnlockCardTitleProps as Props } from './types';
import { Icon } from '@aibees/shared/components';
import { LoadingButton } from '@mui/lab';

export const PeopleAnalysisUnlockCardTitle = memo(({ className }: Props) => {
  const workspace = useWorkspace();
  const hover = useBoolean(false);
  const { track } = useTrackEvents();
  const notify = useNotify();
  const { api } = usePersonReadDataContext();
  const { addProcessingItems } = useActionButtonContext();
  const saveApi = useSaveRecord(SearchType.People);
  const { failed, disabled, text } = usePersonalityAnalysisStatus(
    saveApi,
    api?.data
  );
  const { personalityLoading } = useIsPeopleUnlockButtonLoading(api?.data);

  const handleAnalysis = () => {
    if (personalityLoading) return;
    const payload: SaveRecordRequest = {
      kind: 'ADD',
      actions: [SaveRecordAction.CONTACT_REVEAL_ANALYSIS],
      ids: [api?.data?.id || ''],
      overwriteOwner: false,
      user: workspace?.user,
      labels: [],
    };
    addProcessingItems({
      id: api?.data?.id ?? '',
      type: QUICK_INFO_BUTTONS.PERSONALITY_ANALYSIS,
      state: ProcessingItemState.UNLOCKING,
    });

    saveApi.mutateAsync(payload).then(() => {
      const key = Date.now().toString();
      notify.progress(
        'Your request is in progress',
        'Processing the AI personality. Please wait...',
        key,
        {
          icon: notificationIconMap.AiPersonality,
        }
      );
      track('UNLOCK_PERSONALITY_ANALYSIS', {
        multiple: false,
        retry: failed,
      });
    });
  };

  const handleMouseLeave = () => {
    hover.onFalse();
  };

  const handleMouseEnter = () => {
    hover.onTrue();
  };

  return (
    <Styled.Wrapper
      className={classnames('PeopleAnalysisUnlockCardTitle', className)}
      sx={SX.root}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Icon name="round-person-search" color="primary.500" size={16} />
        <Typography variant="body3" fontWeight={700} color="primary.600">
          AI Personality Insight
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={0.5}>
        {hover.value && (
          <CreditRuleCoin
            type={CreditUsageType.CONTACT_ANALYSIS_REVEAL}
            sx={{ alignItems: 'center', display: 'flex' }}
          />
        )}
        <LoadingButton
          variant="contained"
          size="xsmall"
          color="primary"
          disabled={disabled}
          loading={personalityLoading}
          onClick={handleAnalysis}
          onMouseLeave={handleMouseLeave}
          onMouseEnter={handleMouseEnter}
        >
          {text}
        </LoadingButton>
      </Stack>
    </Styled.Wrapper>
  );
});

PeopleAnalysisUnlockCardTitle.displayName = 'PeopleAnalysisUnlockCardTitle';
